import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Form,
  message,
  Row,
  Col,
  Select,
  Input,
  Button,
  Upload,
  Tag,
  InputNumber,
  Switch,
  Tooltip
} from 'antd'
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import { PHOTO_URL } from '../utils/apiutils'
import { PREDEFINED_TAGS } from '../utils/utils'
import api from '../utils/api'
import { getInventoryProducts } from '../api/inventory'
import {
  normalizeProductStylistIds,
  resolveStylistTagLabel,
  stylistsToSelectOptions
} from '../utils/productStylists'
import heic2any from 'heic2any'
import imageCompression from 'browser-image-compression'
const { Option } = Select

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  height: 100%;
  background-color: #f9f9f9;
`

const Title = styled.h2`
  margin-bottom: 20px;
`

const BackButton = styled(Button)`
  align-self: flex-start;
  margin-bottom: 20px;
`

const ProductFormContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`
const Hint = styled.div`
  font-size: 12px;
  color: #888;
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 24px;
  margin-bottom: 8px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ConsumableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2fr) 130px 150px minmax(140px, 1.2fr) 40px;
  gap: 12px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    & > *:nth-child(5) {
      grid-column: 2;
      justify-self: end;
    }
  }
`

const ConsumableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2fr) 130px 150px minmax(140px, 1.2fr) 40px;
  gap: 12px;
  padding: 8px 0;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    display: none;
  }
`

const ConsumableLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;

  @media (min-width: 769px) {
    display: none;
  }
`

const ProductDetailPage = () => {
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState([])
  const [categories, setCategories] = useState([])
  const [stylists, setStylists] = useState([]) // 新增狀態來保存員工列表
  const { businessId, productId } = useParams()
  const goBack = useGoBack('/products')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // 服務搭配的耗材／商品（每筆：{ productId, quantity, defaultCountAsSales, note }）
  const [consumables, setConsumables] = useState([])
  // 庫存商品下拉選單來源
  const [inventoryProducts, setInventoryProducts] = useState([])

  useEffect(() => {
    if (!businessId) return

    let cancelled = false

    const load = async () => {
      try {
        const [categoriesRes, stylistsRes, inventoryRes] = await Promise.all([
          api.get(`/api/business_B/${businessId}/categories`),
          api.get(`/api/business_B/${businessId}/stylists`),
          // 拿庫存商品作為耗材下拉選單；單頁拿 1000 筆通常足夠
          getInventoryProducts({ page: 1, pageSize: 1000 }).catch(() => null)
        ])
        if (cancelled) return
        setCategories(categoriesRes.data || [])
        const stylistRows = Array.isArray(stylistsRes.data)
          ? stylistsRes.data
          : []
        setStylists(stylistRows)
        const invItems =
          inventoryRes?.data?.data?.items ||
          inventoryRes?.data?.items ||
          []
        setInventoryProducts(invItems)

        if (!productId) return

        const productRes = await api.get(
          `/api/business_B/${businessId}/products/${productId}`
        )
        if (cancelled) return
        const product = productRes.data
        const durationHours = Math.floor(product.duration / 60)
        const durationMinutes = product.duration % 60
        form.setFieldsValue({
          name: product.name,
          durationHours,
          durationMinutes,
          price: product.price,
          originalPrice: product.originalPrice,
          tags: product.tags,
          category: product.category,
          description: product.description,
          stylists: normalizeProductStylistIds(
            product.stylists,
            stylistRows
          ),
          // null 與 undefined 都代表「沿用全店預設」，欄位留空
          bufferMinutes:
            product.bufferMinutes == null ? undefined : product.bufferMinutes,
          stylistSelectionMode: product.stylistSelectionMode || 'inherit'
        })
        // 帶入既有的耗材設定
        if (Array.isArray(product.consumables)) {
          setConsumables(
            product.consumables.map((c) => ({
              productId: String(c.productId || ''),
              quantity: Number(c.quantity) || 1,
              defaultCountAsSales: Boolean(c.defaultCountAsSales),
              note: c.note || ''
            }))
          )
        }
        const initialFileList = product.image
          ? [
              {
                uid: product._id,
                name: product.image.split('/').pop(),
                status: 'done',
                url: `${PHOTO_URL}${product.image}`
              }
            ]
          : []
        setFileList(initialFileList)
      } catch (error) {
        console.error('Error fetching product / categories / stylists:', error)
        message.error('載入服務資料失敗')
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [productId, businessId, form])

  const beforeUpload = (file) => {
    const isAllowedType =
      /\.(jpeg|jpg|png|gif|mp4|mov|quicktime|webp|heic|heif|bmp)$/i.test(
        file.name
      )
    const isLt5M = file.size / 1024 / 1024 < 5

    if (!isAllowedType) {
      message.error(
        '不支持的文件格式！請上傳 JPEG, JPG, PNG, GIF, MP4, MOV, QuickTime, WebP, HEIC, HEIF 或 BMP 格式的文件。'
      )
    }
    if (!isLt5M) {
      message.error('文件必須小於 5MB！')
    }

    return isAllowedType && isLt5M
  }

  const handleFileChange = async ({ fileList: newFileList }) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    }

    try {
      if (newFileList.length > 0 && newFileList[0].originFileObj) {
        const file = newFileList[0].originFileObj

        // 使用檔案名稱來檢查是否為 HEIC/HEIF 格式
        const isHeicHeif = /\.(heic|heif)$/i.test(file.name)

        if (isHeicHeif) {
          message.loading('正在轉換圖片格式...', 0)

          try {
            const jpegBlob = await heic2any({
              blob: file, // 使用原始文件而不是壓縮後的文件
              toType: 'image/jpeg',
              quality: 0.8
            })

            const originalName = file.name
            const baseName = originalName.replace(/\.(heic|heif)$/i, '')
            const newFileName = `${baseName}.jpg`

            const convertedFile = new File([jpegBlob], newFileName, {
              type: 'image/jpeg',
              lastModified: new Date().getTime()
            })

            // 轉換完成後再進行壓縮
            const compressedFile = await imageCompression(
              convertedFile,
              options
            )

            const finalFile = new File([compressedFile], newFileName, {
              type: 'image/jpeg',
              lastModified: new Date().getTime()
            })

            newFileList[0].originFileObj = finalFile
            newFileList[0].name = newFileName

            message.destroy()
            message.success('圖片格式轉換完成')
          } catch (heicError) {
            console.error('HEIC轉換失敗:', heicError)
            throw new Error('HEIC格式轉換失敗')
          }
        } else {
          // 非 HEIC/HEIF 格式，直接壓縮
          const compressedFile = await imageCompression(file, options)
          const newFile = new File([compressedFile], file.name, {
            type: compressedFile.type,
            lastModified: new Date().getTime()
          })
          newFileList[0].originFileObj = newFile
        }

        if (beforeUpload(newFileList[0].originFileObj)) {
          setFileList(newFileList.slice(0, 1))
        }
      } else {
        setFileList(newFileList)
      }
    } catch (error) {
      console.error('圖片轉換失敗:', error)
      message.error('圖片轉換失敗，請使用其他格式的圖片')
      setFileList([])
    }
  }

  // ----- 耗材列表的局部更新 helper -----
  const updateConsumableAt = (idx, patch) => {
    setConsumables((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    )
  }
  const addConsumableRow = () => {
    setConsumables((prev) => [
      ...prev,
      { productId: '', quantity: 1, defaultCountAsSales: false, note: '' }
    ])
  }
  const removeConsumableAt = (idx) => {
    setConsumables((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleFormSubmit = async (values) => {
    setIsSubmitting(true)
    const {
      name,
      durationHours,
      durationMinutes,
      price,
      originalPrice,
      tags,
      category,
      description,
      stylists: selectedStylistIds,
      bufferMinutes,
      stylistSelectionMode
    } = values

    const duration = durationHours * 60 + durationMinutes

    const formData = new FormData()
    formData.append('name', name)
    formData.append('duration', duration)
    formData.append('price', price)
    formData.append('originalPrice', originalPrice)
    formData.append('tags', JSON.stringify(tags))
    formData.append('category', category)
    formData.append('description', description)
    formData.append(
      'stylists',
      JSON.stringify(
        normalizeProductStylistIds(selectedStylistIds, stylists)
      )
    )

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append('image', fileList[0].originFileObj)
    }

    // 過濾掉沒選商品的空白行；後端會驗 productId 必填
    const validConsumables = consumables
      .filter((c) => c.productId)
      .map((c) => ({
        productId: c.productId,
        quantity: Number(c.quantity) || 1,
        defaultCountAsSales: Boolean(c.defaultCountAsSales),
        note: c.note || ''
      }))
    formData.append('consumables', JSON.stringify(validConsumables))

    formData.append(
      'stylistSelectionMode',
      stylistSelectionMode || 'inherit'
    )

    // bufferMinutes：留空（undefined / null / '') 代表「用全店預設」，
    // 後端收到 null/'' 會把欄位設成 null。
    if (bufferMinutes === undefined || bufferMinutes === null || bufferMinutes === '') {
      formData.append('bufferMinutes', '')
    } else {
      formData.append('bufferMinutes', String(bufferMinutes))
    }

    try {
      await api.put(
        `/api/business_B/${businessId}/products/${productId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      message.success('服務更新成功')
      // 保存後留在同頁面，方便繼續調整；如需返回清單請手動點上方「返回」按鈕
    } catch (error) {
      console.error('Error updating product:', error)
      message.error('服務更新失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageContainer>
      <BackButton icon={<ArrowLeftOutlined />} onClick={goBack}>
        返回
      </BackButton>
      <Title>編輯服務</Title>
      <ProductFormContainer>
        <Form form={form} layout='vertical' onFinish={handleFormSubmit}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item name='image' label='產品照片'>
                <Upload
                  listType='picture-card'
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  multiple={false}
                >
                  {fileList.length < 1 && (
                    <div>
                      <PlusOutlined />
                      <div>上傳</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              <div
                style={{
                  marginTop: '-20px',
                  marginBottom: '20px',
                  color: '#888'
                }}
              >
                不支持 TIFF 格式
                <br />
                最多上傳 1 張圖片，大小不超過 5MB
              </div>
            </Col>
            <Col xs={24} sm={24} md={16} lg={18}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='name'
                    label='服務名稱'
                    rules={[{ required: true, message: '請輸入服務名稱' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='price'
                    label='價格（臺幣）'
                    rules={[{ required: true, message: '請輸入價格' }]}
                  >
                    <Input type='number' />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='originalPrice'
                    label='原價（臺幣）'
                    rules={[{ required: true, message: '請輸入原價' }]}
                  >
                    <Input type='number' />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='durationHours'
                    label='進行時間（小時）'
                    rules={[
                      { required: true, message: '請選擇進行時間（小時）' }
                    ]}
                  >
                    <Select>
                      {[...Array(9)].map((_, i) => (
                        <Option key={i} value={i}>
                          {i}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='durationMinutes'
                    label='進行時間（分鐘）'
                    rules={[
                      { required: true, message: '請選擇進行時間（分鐘）' }
                    ]}
                  >
                    <Select>
                      {[0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
                        (minute) => (
                          <Option key={minute} value={minute}>
                            {minute}
                          </Option>
                        )
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='tags'
                    label='標籤'
                    style={{ marginBottom: '0px' }}
                  >
                    <Select mode='multiple'>
                      {PREDEFINED_TAGS.map((tag) => (
                        <Option key={tag} value={tag}>
                          {tag}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Hint>
                    建議3個標籤內，超過部分用戶端會隱藏，預約頁可見全部
                  </Hint>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='category'
                    label='產品分類'
                    rules={[{ required: true, message: '請選擇產品分類' }]}
                  >
                    <Select>
                      {categories.map((category) => (
                        <Option key={category._id} value={category._id}>
                          {category.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name='stylists'
                    label='可服務人員'
                    rules={[{ required: true, message: '請選擇可服務人員' }]}
                    normalize={(v) =>
                      stylists.length
                        ? normalizeProductStylistIds(
                            Array.isArray(v) ? v : v == null ? [] : [v],
                            stylists
                          )
                        : Array.isArray(v)
                          ? v
                          : v == null
                            ? []
                            : [v]
                    }
                  >
                    <Select
                      mode='multiple'
                      optionFilterProp='label'
                      options={stylistsToSelectOptions(stylists)}
                      tagRender={(props) => {
                        const { value: v, closable, onClose } = props
                        return (
                          <Tag
                            closable={closable}
                            onClose={onClose}
                            style={{ marginInlineEnd: 4 }}
                          >
                            {resolveStylistTagLabel(v, stylists)}
                          </Tag>
                        )
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name='description' label='描述'>
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='stylistSelectionMode'
                    label='客人預約時可否指定人員'
                    extra='「由店家指派」的服務，客人線上預約看不到選人那一步，單子會進「待指派」由你安排。'
                  >
                    <Select
                      options={[
                        {
                          label: '依店家預約設定（預設）',
                          value: 'inherit'
                        },
                        { label: '客人自己指定人員', value: 'choose' },
                        {
                          label: '可指定，也可選「不指定人員」',
                          value: 'choose_or_any'
                        },
                        { label: '不給指定，由店家指派', value: 'assigned' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name='bufferMinutes'
                    label='整理時間（覆寫，留空＝用全店預設）'
                    extra='本服務結束後保留的整理／銜接時間。空白＝沿用「商家設定 → 預約設定 → 整理時間」。'
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={240}
                      step={5}
                      addonAfter='分鐘'
                      placeholder='留空＝用全店預設'
                    />
                  </Form.Item>
                </Col>
              </Row>

              <SectionTitle>
                服務搭配的耗材／商品
                <Tooltip
                  title={
                    <div>
                      在這裡設定的商品，結帳時會自動帶入到「商品」欄。
                      <br />
                      <strong>預設不計入商品銷售業績</strong>
                      ，店員結帳時若要算業績，可在結帳頁勾選「加入業績」。
                      <br />
                      若打開下方「預設加入業績」開關，自動帶入時就會預先勾起來。
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#888', cursor: 'help' }} />
                </Tooltip>
              </SectionTitle>
              <Hint style={{ marginBottom: 12 }}>
                例：燙髮服務搭配「燙髮藥水 1 支」、「護髮膜 1 條」等，這些商品會在
                結帳時自動扣庫存，但預設不算銷售業績。
              </Hint>
              {consumables.length === 0 && (
                <Hint style={{ padding: '12px 0' }}>
                  尚未設定任何耗材，請點下方「新增耗材」按鈕。
                </Hint>
              )}
              {consumables.length > 0 && (
                <ConsumableHeader>
                  <div>商品</div>
                  <div>數量</div>
                  <div>預設加入業績</div>
                  <div>備註</div>
                  <div />
                </ConsumableHeader>
              )}
              {consumables.map((row, idx) => {
                // 找到目前選中的庫存商品，把單位（瓶／件／條…）顯示在數量欄右側
                const selectedProduct = inventoryProducts.find(
                  (p) => (p._id || p.id) === row.productId
                )
                const unit = selectedProduct?.unit || '個'
                return (
                  <ConsumableRow key={idx}>
                    <div>
                      <ConsumableLabel>商品</ConsumableLabel>
                      <Select
                        style={{ width: '100%' }}
                        placeholder='選擇庫存商品'
                        showSearch
                        optionFilterProp='label'
                        value={row.productId || undefined}
                        onChange={(val) =>
                          updateConsumableAt(idx, { productId: val })
                        }
                        options={inventoryProducts.map((p) => ({
                          label: `${p.name || p.title || ''}${
                            p.capacity ? ` (${p.capacity})` : ''
                          }`,
                          value: p._id || p.id
                        }))}
                      />
                    </div>
                    <div>
                      <ConsumableLabel>數量</ConsumableLabel>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        // 不指定 step，預設 1；想填 0.5 直接用鍵盤輸入即可。
                        // 這樣 AntD 不會強制把整數補成「1.0」，視覺上就是「1」。
                        value={row.quantity}
                        addonAfter={unit}
                        onChange={(val) =>
                          updateConsumableAt(idx, { quantity: val ?? 1 })
                        }
                      />
                    </div>
                    <div>
                      <ConsumableLabel>預設加入業績</ConsumableLabel>
                      <Switch
                        checked={row.defaultCountAsSales}
                        onChange={(val) =>
                          updateConsumableAt(idx, { defaultCountAsSales: val })
                        }
                      />
                    </div>
                    <div>
                      <ConsumableLabel>備註</ConsumableLabel>
                      <Input
                        placeholder='例：短髮 1 條／長髮 2 條'
                        value={row.note}
                        onChange={(e) =>
                          updateConsumableAt(idx, { note: e.target.value })
                        }
                      />
                    </div>
                    <Button
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeConsumableAt(idx)}
                    />
                  </ConsumableRow>
                )
              })}
              <Button
                type='dashed'
                icon={<PlusOutlined />}
                onClick={addConsumableRow}
                style={{ marginTop: 12, marginBottom: 24 }}
              >
                新增耗材
              </Button>

              <Button
                type='primary'
                htmlType='submit'
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting ? '保存中...' : '保存'}
              </Button>
            </Col>
          </Row>
        </Form>
      </ProductFormContainer>
    </PageContainer>
  )
}

export default ProductDetailPage
